import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, shipping, tax, total } = useCart();
  const { user } = useAuth();
  const { isPrelaunch } = useSiteSettings();
  const nav = useNavigate();

  const checkout = () => {
    if (isPrelaunch) return;
    nav(user ? "/checkout" : "/login?next=/checkout");
  };

  if (items.length === 0) {
    return (
      <div className="container-tight py-24 text-center" data-testid="empty-cart">
        <ShoppingBag className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
        <h1 className="font-display font-black text-3xl mb-2">YOUR CART IS EMPTY</h1>
        <p className="text-zinc-500 mb-6">Fill it up with some serious hardware.</p>
        <Link to="/catalog"><Button data-testid="empty-cart-shop"
          className="bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">
          BROWSE CATALOG <ArrowRight className="w-4 h-4 ml-2" />
        </Button></Link>
      </div>
    );
  }

  return (
    <div className="container-tight py-8" data-testid="cart-page">
      <div className="mb-6">
        <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-1">// CART</div>
        <h1 className="font-display font-black text-3xl md:text-4xl">YOUR CART ({items.length})</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`}
              className="border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm flex gap-4 items-center">
              <img src={it.image} alt={it.name} className="w-20 h-20 object-cover border border-zinc-800" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] tracking-widest text-[#00F0FF]">{it.brand}</div>
                <div className="font-display font-semibold text-white truncate">{it.name}</div>
                <div className="font-mono text-sm text-zinc-400 mt-1">{formatPrice(it.price)} ea</div>
              </div>
              <div className="flex items-center border border-zinc-800 rounded-sm">
                <button data-testid={`qty-minus-${it.product_id}`}
                  onClick={() => updateQty(it.product_id, Math.max(1, it.quantity - 1))}
                  className="w-8 h-9 hover:bg-zinc-900 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                <span className="w-10 text-center font-mono text-sm">{it.quantity}</span>
                <button data-testid={`qty-plus-${it.product_id}`}
                  onClick={() => updateQty(it.product_id, it.quantity + 1)}
                  className="w-8 h-9 hover:bg-zinc-900 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
              </div>
              <div className="font-mono font-bold w-24 text-right">{formatPrice(it.price * it.quantity)}</div>
              <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id)}
                className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <aside className="border border-zinc-800 bg-[#0a0a0c] p-6 rounded-sm h-fit sticky top-24">
          <div className="font-mono text-xs tracking-widest text-zinc-400 uppercase mb-4">Order Summary</div>
          <div className="space-y-2 text-sm">
            <Row label="Subtotal" value={formatPrice(subtotal)} />
            <Row label={shipping === 0 ? "Shipping (FREE)" : "Shipping"} value={formatPrice(shipping)} />
            <Row label="Est. Tax" value={formatPrice(tax)} />
          </div>
          <div className="border-t border-zinc-800 mt-4 pt-4 flex items-baseline justify-between">
            <span className="font-mono text-xs tracking-widest uppercase text-zinc-400">Total</span>
            <span data-testid="cart-total" className="font-mono font-bold text-2xl">{formatPrice(total)}</span>
          </div>
          <Button data-testid="checkout-btn" onClick={checkout} disabled={isPrelaunch}
            className="w-full mt-6 h-12 bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider glow-orange disabled:opacity-40">
            {isPrelaunch ? "COMING SOON" : <>PROCEED TO CHECKOUT <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
          {isPrelaunch && (
            <div className="mt-3 text-xs text-yellow-300/80 font-mono text-center">
              Contact sales@interstate-pc.com for inquiries.
            </div>
          )}
          <div className="font-mono text-[10px] text-zinc-500 mt-3 text-center tracking-widest">{isPrelaunch ? "PRELAUNCH · PREVIEW ONLY" : "SECURE CHECKOUT · STRIPE"}</div>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-zinc-400">
      <span>{label}</span>
      <span className="font-mono text-white">{value}</span>
    </div>
  );
}
